import { createAuthClient } from "@neondatabase/neon-js/auth";
import { BetterAuthReactAdapter } from "@neondatabase/neon-js/auth/react/adapters";

const authUrl = import.meta.env.VITE_NEON_AUTH_URL;
if (!authUrl) {
  throw new Error(
    "VITE_NEON_AUTH_URL is not set. Copy it from the Neon console (Auth > base URL) into apps/web/.env.local",
  );
}

/**
 * Neon Auth (Managed Better Auth) client.
 *
 * The React adapter adds `useSession()`. Sessions ride an httpOnly cookie on the
 * auth domain; `getJWTToken()` exchanges that for the short-lived JWT we send to
 * our own API as a bearer token.
 */
export const authClient = createAuthClient(authUrl, {
  adapter: BetterAuthReactAdapter(),
});

/**
 * Fetches the short-lived JWT we send to our own API.
 *
 * NOT `authClient.getJWTToken()`: the beta SDK (neon-js 0.7.0-beta) requests
 * `/get-jwt-token`, while the deployed Neon Auth service exposes `/token`. That
 * mismatch makes the SDK helper return 404 against the live server, so we call
 * the documented endpoint directly. Revisit once the SDK leaves beta.
 *
 * `credentials: "include"` is required — the session lives in an httpOnly cookie
 * on the auth domain, which is cross-origin to the app.
 */
let cached: { token: string; expiresAt: number } | null = null;

/** Refresh this far ahead of expiry so a request never races the clock. */
const REFRESH_SKEW_MS = 60_000;

function expiryOf(jwt: string): number {
  try {
    const payload = jwt.split(".")[1];
    if (!payload) return 0;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = (JSON.parse(json) as { exp?: number }).exp;
    return exp ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

export async function getAccessToken(): Promise<string | null> {
  if (cached && Date.now() < cached.expiresAt - REFRESH_SKEW_MS) return cached.token;

  const res = await fetch(`${authUrl}/token`, { credentials: "include" });
  if (!res.ok) {
    cached = null;
    return null;
  }

  const { token } = (await res.json()) as { token?: string };
  if (!token) {
    cached = null;
    return null;
  }

  cached = { token, expiresAt: expiryOf(token) };
  return token;
}

/** Drops the cached JWT. Call on sign-out so the next user cannot reuse it. */
export function clearAccessToken(): void {
  cached = null;
}

/**
 * Google sign-in for the mobile shells.
 *
 * Google blocks OAuth inside embedded webviews, and the normal redirect flow
 * would send the browser back to the app's internal `http://tauri.localhost`
 * origin, which only resolves inside the webview. Instead:
 *
 *   1. Ask the auth server for the provider URL with `disableRedirect` — this
 *      also sets the session-challenge cookie in the webview's cookie jar.
 *   2. Open that URL in the system browser (real Chrome, where Google is happy
 *      and the user is already signed in).
 *   3. The auth server finishes the handshake and 302s to our App Link
 *      (`https://sebas.akhsanhakiki.com/app/oauth/callback?...`), which Android
 *      hands back to the app as an `onOpenUrl` event carrying the verifier.
 *   4. Reload the app with `?neon_auth_session_verifier=…`; the Neon Auth client
 *      exchanges it (against the challenge cookie from step 1) for a real
 *      session cookie, and `useSession()` re-renders the app signed in.
 *
 * The path prefix here must match `plugins.deep-link.mobile[].pathPrefix` in
 * `tauri.conf.json` and `.well-known/assetlinks.json`.
 */
const MOBILE_OAUTH_CALLBACK = "https://sebas.akhsanhakiki.com/app/oauth/callback";
const VERIFIER_PARAM = "neon_auth_session_verifier";

export async function signInWithGoogleViaSystemBrowser(): Promise<void> {
  const [{ onOpenUrl }, { openUrl }] = await Promise.all([
    import("@tauri-apps/plugin-deep-link"),
    import("@tauri-apps/plugin-opener"),
  ]);

  const res = await fetch(`${authUrl}/sign-in/social`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      provider: "google",
      callbackURL: MOBILE_OAUTH_CALLBACK,
      disableRedirect: true,
    }),
  });
  if (!res.ok) throw new Error("Could not start Google sign-in. Please try again.");
  const { url } = (await res.json()) as { url?: string };
  if (!url) throw new Error("Could not start Google sign-in. Please try again.");

  const verifier = await new Promise<string>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("Google sign-in timed out. Please try again.")),
      180_000,
    );
    // Registered before `openUrl` so a fast round-trip can't race past it.
    void onOpenUrl((urls) => {
      for (const raw of urls) {
        try {
          const value = new URL(raw).searchParams.get(VERIFIER_PARAM);
          if (value) {
            clearTimeout(timer);
            resolve(value);
            return;
          }
        } catch {
          // Not a URL we care about.
        }
      }
    });
    void openUrl(url).catch((err: unknown) => {
      clearTimeout(timer);
      // Keep the underlying reason visible — a bare "could not open the browser"
      // hid a capability-scope denial (`opener:default` missing) for a while.
      const detail = err instanceof Error ? err.message : String(err);
      reject(new Error(`Could not open the browser: ${detail}`));
    });
  });

  const target = new URL(window.location.href);
  target.searchParams.set(VERIFIER_PARAM, verifier);
  window.location.replace(target.toString());
}
